import { BookOpen, GraduationCap, ListChecks, PlayCircle, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { formatSydneyDateShort } from '../../../utils/helpers';
import { HrBarList } from '../components/BarList';
import { HrDataTable } from '../components/DataTable';
import type { HrColumn } from '../components/DataTable';
import { HrKpiGrid } from '../components/KpiGrid';
import { HrStatusPill } from '../components/StatusPill';
import {
  countBy,
  percent,
  sortCountsDesc,
  trainingPlanStatusTone,
} from '../hr-dashboard-utils';
import type {
  HrCourseAssignment,
  HrTrainingPlan,
} from '../../../types/hr-dashboard.types';
import type { HrSectionProps } from './types';

const PLAN_COLOURS: Record<string, string> = {
  Completed: 'var(--chart-3)',
  'In Progress': 'var(--chart-2)',
  'Not Started': 'var(--muted-foreground)',
};

export function LearningSection({
  data,
  people,
  filters,
  asOf,
  canExport,
  checkExport,
}: HrSectionProps) {
  const ids = new Set(people.map((person) => person.id));
  const assignments = data.courseAssignments.filter((row) => ids.has(row.userId));
  const plans = data.trainingPlans.filter((row) => ids.has(row.traineeId));

  const completedAssignments = assignments.filter((row) => row.status === 'Completed').length;
  const completionRate = assignments.length
    ? percent(completedAssignments, assignments.length)
    : null;

  const completedPlans = plans.filter((plan) => plan.status === 'Completed').length;
  const notStartedPlans = plans.filter((plan) => plan.status === 'Not Started').length;

  const courseCompletion = sortCountsDesc(
    countBy(assignments, (assignment) => assignment.course)
  ).map(([label]) => {
    const rows = assignments.filter((assignment) => assignment.course === label);
    const done = rows.filter((assignment) => assignment.status === 'Completed').length;
    return {
      key: label,
      label,
      value: percent(done, rows.length),
      hint: `${done} of ${rows.length} completed`,
    };
  });

  const trainerItems = sortCountsDesc(
    countBy(plans, (plan) => plan.trainer || 'Unassigned')
  ).map(([label, value]) => ({ key: label, label, value }));

  const assignmentColumns: HrColumn<HrCourseAssignment>[] = [
    { key: 'user', header: 'Employee', value: (row) => row.user },
    { key: 'dept', header: 'Department', value: (row) => row.dept || '—' },
    { key: 'course', header: 'Course', value: (row) => row.course },
    {
      key: 'status',
      header: 'Status',
      value: (row) => row.status,
      render: (row) => (
        <HrStatusPill
          tone={
            row.status === 'Completed'
              ? 'success'
              : row.status === 'In Progress'
                ? 'warning'
                : 'muted'
          }
        >
          {row.status}
        </HrStatusPill>
      ),
    },
    {
      key: 'assigned',
      header: 'Assigned',
      value: (row) => row.assigned || '',
      render: (row) => formatSydneyDateShort(row.assigned) || '—',
    },
    {
      key: 'due',
      header: 'Due',
      value: (row) => row.due || '',
      render: (row) => formatSydneyDateShort(row.due) || '—',
    },
    {
      key: 'completed',
      header: 'Completed',
      value: (row) => row.completed || '',
      render: (row) => formatSydneyDateShort(row.completed) || '—',
    },
    {
      key: 'progress',
      header: 'Progress',
      align: 'right',
      value: (row) => row.progress,
      render: (row) => <span className="tabular-nums">{row.progress}%</span>,
    },
  ];

  const planColumns: HrColumn<HrTrainingPlan>[] = [
    { key: 'name', header: 'Plan', value: (row) => row.name },
    { key: 'trainee', header: 'Trainee', value: (row) => row.trainee },
    { key: 'dept', header: 'Department', value: (row) => row.dept || '—' },
    { key: 'trainer', header: 'Trainer', value: (row) => row.trainer || '—' },
    {
      key: 'status',
      header: 'Status',
      value: (row) => row.status,
      render: (row) => (
        <HrStatusPill tone={trainingPlanStatusTone(row.status)}>{row.status}</HrStatusPill>
      ),
    },
    {
      key: 'started',
      header: 'Started',
      value: (row) => row.started || '',
      render: (row) => formatSydneyDateShort(row.started) || '—',
    },
    {
      key: 'completed',
      header: 'Completed',
      value: (row) => row.completed || '',
      render: (row) => formatSydneyDateShort(row.completed) || '—',
    },
    {
      key: 'procs',
      header: 'Procedures',
      align: 'right',
      value: (row) => row.procs,
    },
  ];

  const planCounts = countBy(plans, (plan) => plan.status);
  const planParts = ['Completed', 'In Progress', 'Not Started'].map((status) => ({
    label: status,
    value: planCounts.get(status) || 0,
    color: PLAN_COLOURS[status],
  }));
  const planTotal = planParts.reduce((total, part) => total + part.value, 0);

  return (
    <div className="space-y-4">
      <HrKpiGrid
        items={[
          {
            label: 'Course assignments',
            value: assignments.length,
            detail: assignments.length ? `${assignments.length} learners` : 'Awaiting first assignments',
            icon: <BookOpen className="h-5 w-5" />,
          },
          {
            label: 'Completion rate',
            value: completionRate === null ? '—' : completionRate,
            unit: completionRate === null ? undefined : '%',
            detail: 'Shown once courses are assigned',
            icon: <GraduationCap className="h-5 w-5" />,
          },
          {
            label: 'Training plans',
            value: plans.length,
            icon: <ListChecks className="h-5 w-5" />,
          },
          {
            label: 'Plans completed',
            value: completedPlans,
            tone: 'success',
            icon: <PlayCircle className="h-5 w-5" />,
          },
          {
            label: 'Plans not started',
            value: notStartedPlans,
            tone: notStartedPlans > 0 ? 'warning' : 'primary',
            icon: <Users className="h-5 w-5" />,
          },
        ]}
      />

      {assignments.length === 0 ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="text-base">Learning assignments are waiting for data</CardTitle>
            <CardDescription>
              The Learning module was only recently launched, so there are no course assignments
              yet. Once they start, this panel shows:
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>Assigned, completed and overdue courses by course</li>
              <li>Completion rate by department and role level</li>
              <li>Overdue learners to chase</li>
              <li>Certificates issued this fortnight</li>
            </ul>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Course completion</CardTitle>
              <CardDescription>Percentage completed by course</CardDescription>
            </CardHeader>
            <CardContent>
              <HrBarList
                items={courseCompletion}
                max={100}
                formatter={(value) => `${value}%`}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Course assignments</CardTitle>
              <CardDescription>Assigned, in progress and completed courses</CardDescription>
            </CardHeader>
            <CardContent className="p-3">
              <HrDataTable
                rows={assignments}
                columns={assignmentColumns}
                initialSort={{ key: 'due', direction: 'asc' }}
                searchPlaceholder="Search employee or course…"
                emptyLabel="No course assignments found"
                viewName="Course assignments"
                filters={filters}
                asOf={asOf}
                canExport={canExport}
                onBeforeExport={checkExport}
                rowKey={(row) => row.id}
              />
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Training plan status</CardTitle>
            <CardDescription>On-the-job plans in Idarah Training</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex h-5 overflow-hidden rounded-md bg-muted">
                {planParts.map((part) =>
                  part.value > 0 ? (
                    <div
                      key={part.label}
                      title={`${part.label}: ${part.value}`}
                      style={{
                        width: `${percent(part.value, planTotal || 1)}%`,
                        backgroundColor: part.color,
                      }}
                    />
                  ) : null
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {planParts.map((part) => (
                  <span key={part.label} className="inline-flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: part.color }}
                    />
                    <span className="text-muted-foreground">{part.label}</span>
                    <strong className="tabular-nums text-foreground">{part.value}</strong>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">Plans by trainer</h3>
              <HrBarList items={trainerItems} />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-7">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Training plans</CardTitle>
            <CardDescription>On-the-job training in progress</CardDescription>
          </CardHeader>
          <CardContent className="p-3">
            <HrDataTable
              rows={plans}
              columns={planColumns}
              initialSort={{ key: 'started', direction: 'desc' }}
              searchPlaceholder="Search plans, trainees or trainers…"
              emptyLabel="No training plans found"
              viewName="Training plans"
              filters={filters}
              asOf={asOf}
              canExport={canExport}
              onBeforeExport={checkExport}
              rowKey={(row) => row.id}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
